import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, Redirect, useHistory } from "react-router-dom";
import {
  FaBullhorn,
  FaEdit,
  FaExternalLinkAlt,
  FaFilePdf,
  FaHome,
  FaImage,
  FaLink,
  FaPlus,
  FaSyncAlt,
  FaTasks,
  FaTrashAlt,
  FaUserGraduate,
  FaUserTie,
} from "react-icons/fa";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import COLORS from "../resources/colors/COLORS";
import BackButton from "../ChildComponent/BackButton";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";
import "../css/Admin_css/ManageNotice.css";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;

const PAGE_FONT =
  'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

// Created By Cursor
function getAuthContext() {
  const schoolCode = SessionManager.getSchoolCode();
  const sessions = SessionManager.getCurrentSession();
  const sessionId = Array.isArray(sessions) ? sessions[0]?.session_id : "";
  const staff = SessionManager.getStaffDetails() || {};
  return {
    schoolCode,
    sessionId,
    staffId: staff.staff_id,
    loginType: staff.login_type,
  };
}

// Created By Cursor
function resolveMediaUrl(pathOrUrl) {
  const raw = String(pathOrUrl || "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  const host = String(process.env.REACT_APP_SERVER_NAME || "").replace(/\/$/, "");
  if (!host) return raw;
  return `${host}/${raw.replace(/^\//, "")}`;
}

// Created By Cursor
function formatNoticeDate(item) {
  if (item?.notice_date2) return item.notice_date2;
  const raw = item?.notice_date || item?.date_added || "";
  if (!raw) return "";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return String(raw).slice(0, 10);
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

// Created By Cursor
function formatLinkLabel(url) {
  try {
    const u = new URL(url);
    const path = u.pathname === "/" ? "" : u.pathname;
    const label = `${u.host}${path}`;
    return label.length > 48 ? `${label.slice(0, 45)}…` : label;
  } catch (err) {
    return url.length > 48 ? `${url.slice(0, 45)}…` : url;
  }
}

// Created By Cursor
function getNoticeMedia(item) {
  const attachUrl = resolveMediaUrl(
    item?.attachment_url || item?.attachment_path || ""
  );
  const attachName = item?.attachment_name || "Attachment";
  const attachType = String(item?.attachment_type || "").toLowerCase();
  let noticeUrl = String(item?.notice_url || "").trim();
  if (noticeUrl && noticeUrl.toLowerCase() !== "null") {
    if (!/^https?:\/\//i.test(noticeUrl)) noticeUrl = `https://${noticeUrl}`;
  } else {
    noticeUrl = "";
  }
  const isImage =
    attachType === "image" ||
    attachType.startsWith("image/") ||
    /\.(jpe?g|png|gif|webp)$/i.test(attachName) ||
    /\.(jpe?g|png|gif|webp)$/i.test(attachUrl);
  const isPdf =
    attachType === "pdf" ||
    attachType === "application/pdf" ||
    /\.pdf$/i.test(attachName) ||
    /\.pdf$/i.test(attachUrl);
  return { attachUrl, attachName, attachType, noticeUrl, isImage, isPdf };
}

// Created By Cursor
function ManageNoticeCard({
  item,
  index,
  busyId,
  onToggleStatus,
  onEdit,
  onDelete,
}) {
  const text = item?.notice || "—";
  const by = item?.ordered_by || "—";
  const date = formatNoticeDate(item);
  const status = String(item?.status || "").trim() || "—";
  const isActive = status.toLowerCase() === "active";
  const noticeId = item?.notice_id;
  const busy = busyId === noticeId;

  const { attachUrl, attachName, noticeUrl, isImage, isPdf } =
    getNoticeMedia(item);
  const hasMedia = Boolean(attachUrl || noticeUrl);

  return (
    <article
      className={`mn-card${!isActive ? " is-inactive" : ""}${
        hasMedia ? " has-media" : ""
      }`}
    >
      <div className="mn-card__main">
        <div className="mn-card__top">
          <div className="mn-card__index" aria-hidden>
            {index + 1}
          </div>
          <div className="mn-card__meta">
            <span className="mn-chip">{by}</span>
            {date ? <span className="mn-chip mn-chip--soft">{date}</span> : null}
            <span
              className={`mn-chip mn-chip--status${
                isActive ? " is-active" : " is-inactive"
              }`}
            >
              {status}
            </span>
            {attachUrl && isImage ? (
              <span className="mn-chip mn-chip--image">
                <FaImage aria-hidden /> Image
              </span>
            ) : null}
            {attachUrl && isPdf ? (
              <span className="mn-chip mn-chip--pdf">
                <FaFilePdf aria-hidden /> PDF
              </span>
            ) : null}
            {noticeUrl ? (
              <span className="mn-chip mn-chip--url">
                <FaLink aria-hidden /> Link
              </span>
            ) : null}
          </div>
        </div>

        <p className="mn-card__text">{text}</p>

        {hasMedia ? (
          <div className="mn-card__media">
            {attachUrl && isImage ? (
              <a
                className="mn-media-image"
                href={attachUrl}
                target="_blank"
                rel="noopener noreferrer"
                title={attachName}
              >
                <img src={attachUrl} alt={attachName} />
                <span className="mn-media-image__caption">
                  <FaImage aria-hidden />
                  {attachName}
                </span>
              </a>
            ) : null}

            {attachUrl && !isImage ? (
              <a
                className={`mn-media-file${isPdf ? " is-pdf" : ""}`}
                href={attachUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                <span className="mn-media-file__icon" aria-hidden>
                  {isPdf ? <FaFilePdf /> : <FaExternalLinkAlt />}
                </span>
                <span className="mn-media-file__text">
                  <strong>{attachName}</strong>
                  <small>{isPdf ? "Open PDF" : "Open attachment"}</small>
                </span>
                <FaExternalLinkAlt className="mn-media-file__go" aria-hidden />
              </a>
            ) : null}

            {noticeUrl ? (
              <a
                className="mn-media-file is-url"
                href={noticeUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                <span className="mn-media-file__icon" aria-hidden>
                  <FaLink />
                </span>
                <span className="mn-media-file__text">
                  <strong>Related link</strong>
                  <small>{formatLinkLabel(noticeUrl)}</small>
                </span>
                <FaExternalLinkAlt className="mn-media-file__go" aria-hidden />
              </a>
            ) : null}
          </div>
        ) : null}

        <div className="mn-card__toggle">
          <span className="mn-card__toggle-label">
            {isActive ? "Visible on board" : "Hidden from board"}
          </span>
          <label className="mn-switch">
            <input
              type="checkbox"
              checked={isActive}
              disabled={busy || !noticeId}
              onChange={() => onToggleStatus(item)}
              aria-label={`Toggle notice ${noticeId || ""}`}
            />
            <span className="mn-switch__slider" aria-hidden />
          </label>
        </div>
      </div>

      <div className="mn-card__actions">
        <button
          type="button"
          className="mn-icon-btn mn-icon-btn--edit"
          onClick={() => onEdit(item)}
          disabled={busy}
          title="Edit notice"
          aria-label="Edit notice"
        >
          <FaEdit />
        </button>
        <button
          type="button"
          className="mn-icon-btn mn-icon-btn--delete"
          onClick={() => onDelete(item)}
          disabled={busy}
          title="Delete notice"
          aria-label="Delete notice"
        >
          <FaTrashAlt />
        </button>
      </div>
    </article>
  );
}

// Created By Cursor
export default function ManageNotice() {
  const history = useHistory();
  const loggedIn = localStorage.getItem("token") != null;

  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState("");
  const [studentNotices, setStudentNotices] = useState([]);
  const [staffNotices, setStaffNotices] = useState([]);
  const [activeTab, setActiveTab] = useState("student");
  const [search, setSearch] = useState("");

  const [editOpen, setEditOpen] = useState(false);
  const [editNoticeId, setEditNoticeId] = useState("");
  const [editNotice, setEditNotice] = useState("");
  const [editOrderedBy, setEditOrderedBy] = useState("");
  const [editNoticeUrl, setEditNoticeUrl] = useState("");
  const [editExistingAttachUrl, setEditExistingAttachUrl] = useState("");
  const [editExistingAttachName, setEditExistingAttachName] = useState("");
  const [editExistingIsImage, setEditExistingIsImage] = useState(false);
  const [editExistingIsPdf, setEditExistingIsPdf] = useState(false);
  const [editAttachFile, setEditAttachFile] = useState(null);
  const [editAttachPreview, setEditAttachPreview] = useState("");
  const [editClearAttachment, setEditClearAttachment] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);

  const loadNotices = useCallback(async () => {
    const { schoolCode, sessionId, staffId, loginType } = getAuthContext();
    if (!schoolCode || !sessionId) {
      myToast.notify_warning("Missing school or session details.");
      setStudentNotices([]);
      setStaffNotices([]);
      return;
    }

    setLoading(true);
    const params = {
      school_code: schoolCode,
      session_id: sessionId,
      staff_id: staffId,
      login_type: loginType,
    };

    try {
      const [studentRes, staffRes] = await Promise.all([
        axios
          .get(`${API_BASE()}getStudentAllNotice.php`, { params })
          .then((res) => res.data),
        axios
          .get(`${API_BASE()}getStaffAllNotice.php`, { params })
          .then((res) => res.data),
      ]);

      if (studentRes?.api_status === "OK") {
        setStudentNotices(Array.isArray(studentRes.data) ? studentRes.data : []);
      } else {
        setStudentNotices([]);
        if (studentRes?.api_remark) myToast.notify_warning(studentRes.api_remark);
      }

      if (staffRes?.api_status === "OK") {
        setStaffNotices(Array.isArray(staffRes.data) ? staffRes.data : []);
      } else {
        setStaffNotices([]);
        if (staffRes?.api_remark) myToast.notify_warning(staffRes.api_remark);
      }
    } catch (error) {
      console.error(error);
      setStudentNotices([]);
      setStaffNotices([]);
      myToast.notify_warning("Failed to load notices.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadNotices();
  }, [loadNotices]);

  const activeList = useMemo(
    () => (activeTab === "student" ? studentNotices : staffNotices),
    [activeTab, studentNotices, staffNotices]
  );

  const filteredList = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return activeList;
    return activeList.filter((item) => {
      const hay = [
        item?.notice,
        item?.ordered_by,
        item?.status,
        item?.notice_id,
        item?.notice_url,
      ]
        .map((v) => String(v || "").toLowerCase())
        .join(" ");
      return hay.includes(q);
    });
  }, [activeList, search]);

  const activeCount = useMemo(
    () =>
      activeList.filter(
        (n) => String(n?.status || "").toLowerCase() === "active"
      ).length,
    [activeList]
  );

  const toggleStatus = async (item) => {
    const noticeId = item?.notice_id;
    if (!noticeId) return;
    const { schoolCode } = getAuthContext();
    const current = String(item.status || "");
    const next = current === "Active" ? "InActive" : "Active";

    setBusyId(noticeId);
    try {
      const response = await axios
        .get(`${API_BASE()}updateStudentNotice.php`, {
          params: {
            school_code: schoolCode,
            notice_id: noticeId,
            status: next,
          },
        })
        .then((res) => res.data);

      if (response?.api_status === "OK") {
        myToast.notify_success(
          next === "Active" ? "Notice activated." : "Notice deactivated."
        );
        await loadNotices();
      } else {
        myToast.notify_warning(response?.api_remark || "Could not update status.");
      }
    } catch (error) {
      console.error(error);
      myToast.notify_warning("Failed to update notice status.");
    } finally {
      setBusyId("");
    }
  };

  const resetEditForm = () => {
    setEditNoticeId("");
    setEditNotice("");
    setEditOrderedBy("");
    setEditNoticeUrl("");
    setEditExistingAttachUrl("");
    setEditExistingAttachName("");
    setEditExistingIsImage(false);
    setEditExistingIsPdf(false);
    setEditAttachFile(null);
    setEditAttachPreview("");
    setEditClearAttachment(false);
    const input = document.getElementById("mn_edit_file");
    if (input) input.value = "";
  };

  const openEdit = (item) => {
    const media = getNoticeMedia(item);
    setEditNoticeId(item?.notice_id || "");
    setEditNotice(item?.notice || "");
    setEditOrderedBy(item?.ordered_by || "");
    setEditNoticeUrl(item?.notice_url || media.noticeUrl || "");
    setEditExistingAttachUrl(media.attachUrl);
    setEditExistingAttachName(media.attachName);
    setEditExistingIsImage(media.isImage);
    setEditExistingIsPdf(media.isPdf);
    setEditAttachFile(null);
    setEditAttachPreview("");
    setEditClearAttachment(false);
    setEditOpen(true);
  };

  const closeEdit = () => {
    if (savingEdit) return;
    setEditOpen(false);
    resetEditForm();
  };

  const handleEditAttachChange = (e) => {
    const file = e.target.files?.[0] || null;
    if (!file) {
      setEditAttachFile(null);
      setEditAttachPreview("");
      return;
    }
    const allowed = [
      "application/pdf",
      "image/jpeg",
      "image/png",
      "image/gif",
      "image/webp",
    ];
    const nameOk = /\.(pdf|jpe?g|png|gif|webp)$/i.test(file.name || "");
    if (!allowed.includes(file.type) && !nameOk) {
      myToast.notify_warning("Only PDF or image files are allowed.");
      e.target.value = "";
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      myToast.notify_warning("File too large. Max 10MB.");
      e.target.value = "";
      return;
    }
    setEditAttachFile(file);
    setEditClearAttachment(false);
    if (file.type.startsWith("image/") || /\.(jpe?g|png|gif|webp)$/i.test(file.name)) {
      const reader = new FileReader();
      reader.onloadend = () => setEditAttachPreview(String(reader.result || ""));
      reader.readAsDataURL(file);
    } else {
      setEditAttachPreview("");
    }
  };

  const clearEditAttachment = () => {
    setEditAttachFile(null);
    setEditAttachPreview("");
    setEditClearAttachment(true);
    const input = document.getElementById("mn_edit_file");
    if (input) input.value = "";
  };

  const saveEdit = async () => {
    const noticeText = editNotice.trim();
    const orderedBy = editOrderedBy.trim();
    if (!editNoticeId) return;
    if (!noticeText) {
      myToast.notify_warning("Notice text is required.");
      return;
    }
    if (!orderedBy) {
      myToast.notify_warning("Ordered by is required.");
      return;
    }

    let urlTrim = editNoticeUrl.trim();
    if (urlTrim && !/^https?:\/\//i.test(urlTrim)) {
      urlTrim = `https://${urlTrim}`;
    }

    const { schoolCode, staffId } = getAuthContext();
    setSavingEdit(true);
    try {
      const formData = new FormData();
      formData.append("school_code", schoolCode);
      formData.append("notice_id", editNoticeId);
      formData.append("notice", noticeText);
      formData.append("order_by", orderedBy);
      formData.append("staff_id", staffId || "");
      formData.append("notice_url", urlTrim);
      if (editAttachFile) {
        formData.append("attachment", editAttachFile);
      } else if (editClearAttachment) {
        formData.append("clear_attachment", "1");
      }

      const response = await axios
        .post(`${API_BASE()}updateNotice.php`, formData)
        .then((res) => res.data);

      if (response?.api_status === "OK") {
        myToast.notify_success("Notice updated.");
        setEditOpen(false);
        resetEditForm();
        await loadNotices();
      } else {
        myToast.notify_warning(response?.api_remark || "Could not update notice.");
      }
    } catch (error) {
      console.error(error);
      myToast.notify_warning("Failed to update notice.");
    } finally {
      setSavingEdit(false);
    }
  };

  const deleteNotice = async (item) => {
    const noticeId = item?.notice_id;
    if (!noticeId) return;
    const ok = window.confirm(
      "Delete this notice permanently? This cannot be undone."
    );
    if (!ok) return;

    const { schoolCode } = getAuthContext();
    setBusyId(noticeId);
    try {
      const body = new URLSearchParams();
      body.append("notice_id", noticeId);
      body.append("school_code", schoolCode);

      const response = await axios
        .post(`${API_BASE()}delStudentNotice.php`, body, {
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
        })
        .then((res) => res.data);

      if (response?.api_status === "OK") {
        myToast.notify_success("Notice deleted.");
        await loadNotices();
      } else {
        myToast.notify_warning(response?.api_remark || "Could not delete notice.");
      }
    } catch (error) {
      console.error(error);
      myToast.notify_warning("Failed to delete notice.");
    } finally {
      setBusyId("");
    }
  };

  if (!loggedIn) {
    return <Redirect to="/Login" />;
  }

  return (
    <div
      className="content payroll-page sm-page mn-page"
      style={{ fontFamily: PAGE_FONT }}
    >
      <div
        className="content-header d-flex justify-content-between align-items-center"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header d-flex align-items-center gap-2">
          <button
            type="button"
            className="qb-home-btn"
            aria-label="Home"
            onClick={() => history.push("/AdminMain")}
          >
            <FaHome />
          </button>
          <ul className="breadcrumbs mb-0">
            <li>
              <button
                type="button"
                className="mn-crumb-link"
                onClick={() => history.push("/AdminMain/ActivityManagement")}
              >
                Activity Management
              </button>
            </li>
            <li>
              <button
                type="button"
                className="mn-crumb-link"
                onClick={() => history.push("/AdminMain/NoticeBoard/1")}
              >
                Notice Board
              </button>
            </li>
            <li>
              <span style={{ color: COLORS.white }}>Manage Notice</span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div className="bg_page_white_rounded box_shadow animated fadeInUp sm-shell mn-shell">
        <header className="sm-hero" style={{ "--sm-hero-bg": HERO_GRADIENT }}>
          <div className="sm-hero__brand">
            <div className="sm-hero__icon" aria-hidden>
              <FaTasks />
            </div>
            <div className="min-w-0">
              <h1 className="sm-hero__title">Manage Notice</h1>
              <p className="sm-hero__sub">
                {`${studentNotices.length} student · ${staffNotices.length} staff · ${activeCount} active in tab`}
              </p>
            </div>
          </div>
          <div className="sm-hero__tools">
            <div className="sm-hero__search-group">
              <input
                type="search"
                className="sm-hero__search"
                placeholder="Search notices…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Search notices"
              />
            </div>
            <Link
              to="/AdminMain/NoticeBoard/1"
              className="mn-hero-btn"
            >
              <FaBullhorn aria-hidden /> Board
            </Link>
            <Link
              to="/AdminMain/AddNotice"
              className="mn-hero-btn mn-hero-btn--solid"
            >
              <FaPlus aria-hidden /> Add
            </Link>
            <button
              type="button"
              className={`pp-icon-btn${loading ? " spinning" : ""}`}
              onClick={loadNotices}
              disabled={loading}
              aria-label="Refresh"
              title="Refresh"
            >
              <FaSyncAlt aria-hidden />
            </button>
          </div>
        </header>

        <div className="mn-body">
          <div className="mn-tabs" role="tablist" aria-label="Notice type">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "student"}
              className={`mn-tab${activeTab === "student" ? " is-active" : ""}`}
              onClick={() => setActiveTab("student")}
            >
              <FaUserGraduate aria-hidden />
              Student
              <em>{studentNotices.length}</em>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "staff"}
              className={`mn-tab${activeTab === "staff" ? " is-active" : ""}`}
              onClick={() => setActiveTab("staff")}
            >
              <FaUserTie aria-hidden />
              Staff
              <em>{staffNotices.length}</em>
            </button>
          </div>

          {loading && !filteredList.length ? (
            <div className="mn-loading">
              <MyLoader />
            </div>
          ) : null}

          {!loading && !filteredList.length ? (
            <div className="mn-empty">
              <div className="mn-empty__circle" aria-hidden>
                <FaTasks />
              </div>
              <p className="mn-empty__title">
                {search.trim() ? "No matching notices" : "No notices to manage"}
              </p>
              <p className="mn-empty__hint">
                {search.trim()
                  ? "Try a different search term."
                  : activeTab === "student"
                    ? "No student notices for this session."
                    : "No staff notices for this session."}
              </p>
              {!search.trim() ? (
                <Link to="/AdminMain/AddNotice" className="mn-empty__cta">
                  <FaPlus aria-hidden /> Add Notice
                </Link>
              ) : null}
            </div>
          ) : null}

          {filteredList.length ? (
            <div className="mn-list">
              {filteredList.map((item, index) => (
                <ManageNoticeCard
                  key={item.notice_id || `${activeTab}-${index}`}
                  item={item}
                  index={index}
                  busyId={busyId}
                  onToggleStatus={toggleStatus}
                  onEdit={openEdit}
                  onDelete={deleteNotice}
                />
              ))}
            </div>
          ) : null}

          <div className="mn-actions">
            <Link
              to="/AdminMain/NoticeBoard/1"
              className="mn-btn mn-btn--secondary"
            >
              <FaBullhorn aria-hidden /> Notice Board
            </Link>
            <Link to="/AdminMain/AddNotice" className="mn-btn mn-btn--primary">
              <FaPlus aria-hidden /> Add Notice
            </Link>
          </div>
        </div>
      </div>

      {editOpen ? (
        <div
          className="mn-modal-backdrop"
          role="presentation"
          onClick={closeEdit}
        >
          <div
            className="mn-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="mn-edit-title"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="mn-modal__head">
              <h2 id="mn-edit-title">Update Notice</h2>
              <button
                type="button"
                className="mn-modal__close"
                onClick={closeEdit}
                disabled={savingEdit}
                aria-label="Close"
              >
                ×
              </button>
            </header>
            <div className="mn-modal__body">
              <label className="mn-field">
                <span className="mn-field__label">Notice</span>
                <textarea
                  className="mn-textarea"
                  rows={5}
                  value={editNotice}
                  onChange={(e) => setEditNotice(e.target.value)}
                  disabled={savingEdit}
                />
              </label>
              <label className="mn-field">
                <span className="mn-field__label">Ordered by</span>
                <input
                  type="text"
                  className="mn-input"
                  value={editOrderedBy}
                  onChange={(e) => setEditOrderedBy(e.target.value)}
                  disabled={savingEdit}
                />
              </label>
              <label className="mn-field">
                <span className="mn-field__label">
                  <FaLink aria-hidden /> Link / URL
                </span>
                <input
                  type="url"
                  className="mn-input"
                  placeholder="https://example.com"
                  value={editNoticeUrl}
                  onChange={(e) => setEditNoticeUrl(e.target.value)}
                  disabled={savingEdit}
                />
              </label>

              <div className="mn-field">
                <span className="mn-field__label">
                  <FaImage aria-hidden /> Attachment (PDF / image)
                </span>
                <div className="mn-attach">
                  <label className="mn-file">
                    <span className="mn-file__btn">Choose file</span>
                    <span className="mn-file__hint">
                      {editAttachFile
                        ? editAttachFile.name
                        : !editClearAttachment && editExistingAttachName
                          ? editExistingAttachName
                          : "Max 10MB · PDF, JPG, PNG, GIF, WEBP"}
                    </span>
                    <input
                      id="mn_edit_file"
                      className="mn-file__input"
                      type="file"
                      accept=".pdf,image/jpeg,image/png,image/gif,image/webp"
                      onChange={handleEditAttachChange}
                      disabled={savingEdit}
                    />
                  </label>
                  {(editAttachFile ||
                    (!editClearAttachment && editExistingAttachUrl)) && (
                    <button
                      type="button"
                      className="mn-attach__clear"
                      onClick={clearEditAttachment}
                      disabled={savingEdit}
                    >
                      Remove file
                    </button>
                  )}
                </div>

                {editAttachPreview ? (
                  <div className="mn-attach__preview">
                    <img src={editAttachPreview} alt="New attachment preview" />
                  </div>
                ) : null}

                {!editAttachPreview &&
                editAttachFile &&
                /\.pdf$/i.test(editAttachFile.name) ? (
                  <div className="mn-attach__pdf">
                    <FaFilePdf aria-hidden />
                    <span>{editAttachFile.name}</span>
                  </div>
                ) : null}

                {!editAttachFile &&
                !editClearAttachment &&
                editExistingAttachUrl &&
                editExistingIsImage ? (
                  <div className="mn-attach__preview">
                    <img
                      src={editExistingAttachUrl}
                      alt={editExistingAttachName || "Current attachment"}
                    />
                  </div>
                ) : null}

                {!editAttachFile &&
                !editClearAttachment &&
                editExistingAttachUrl &&
                !editExistingIsImage ? (
                  <div className="mn-attach__pdf">
                    {editExistingIsPdf ? (
                      <FaFilePdf aria-hidden />
                    ) : (
                      <FaExternalLinkAlt aria-hidden />
                    )}
                    <a
                      href={editExistingAttachUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {editExistingAttachName || "Current attachment"}
                    </a>
                  </div>
                ) : null}
              </div>
            </div>
            <footer className="mn-modal__foot">
              <button
                type="button"
                className="mn-btn mn-btn--secondary"
                onClick={closeEdit}
                disabled={savingEdit}
              >
                Cancel
              </button>
              <button
                type="button"
                className="mn-btn mn-btn--primary"
                onClick={saveEdit}
                disabled={savingEdit}
              >
                {savingEdit ? "Saving…" : "Save changes"}
              </button>
            </footer>
          </div>
        </div>
      ) : null}
    </div>
  );
}
